// src/components/RoomListPage.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  ListGroup,
  Form,
  Offcanvas,
  Button,
  Badge,
  Placeholder,
} from 'react-bootstrap';
import { Baby, CircleAlert, CircleCheck, CircleChevronRight, CloudMoon, Filter, Info, Search, User, Users } from 'lucide-react';
import { getFirestore, collection, getDocs, query, orderBy, where, Timestamp } from 'firebase/firestore';
import { db } from '../FireBase'; // your firebase init
import '../css/RoomListPage.css';
import SearchModal from '../components/SearchModal';
import { useNavigate } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';



const PRICE_RANGES = [
  { key: 'all',      label: 'All Prices' },
  { key: '0-2000',   label: 'Up to ₹2,000' },
  { key: '2000-3000',label: '₹2,000–₹3,000' },
  { key: '3000+',    label: 'Above ₹3,000' },
];


export default function RoomListPage() {
  const [range, setRange]               = useState({ from: undefined, to: undefined });
  const [rooms, setRooms]               = useState([]);
  const [imgLoaded, setImgLoaded]       = useState({});                  // track each image load
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [types, setTypes]               = useState([]);
  const [selectedType, setSelectedType] = useState('All Rooms');
  const [priceRange, setPriceRange]     = useState('all');
  const [filtered, setFiltered]         = useState([]);
  const [showOffcanvas, setShowOffcanvas]   = useState(false);
  const [loading, setLoading]           = useState(true);
  const [isMobile, setIsMobile]         = useState(window.innerWidth < 768);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const STORAGE_KEY = 'bookingSearch';
  const [bookingCounts, setBookingCounts] = useState({}); // { [roomId]: number }
  const navigate = useNavigate();
  // Show search on first load if no data
  useEffect(() => {
    if (!localStorage.getItem(STORAGE_KEY)) {
      setShowSearchModal(true);
    }
  }, []);

  // Hydrate range&guests after modal closes
// Hydrate range & guests on mount
useEffect(() => {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (!saved) return;

  try {
    const { from, to, adults: a, children: c } = JSON.parse(saved);

    setRange({
      from: from ? new Date(from) : undefined,
      to:   to   ? new Date(to)   : undefined,
    });

    if (typeof a === "number" && a >= 1) {
      setAdults(a);
    }
    if (typeof c === "number" && c >= 0) {
      setChildren(c);
    }
  } catch (err) {
    console.error("Failed to parse saved search data:", err);
  }
}, [showSearchModal]); // 👈 run every time modal closes



  // Responsive listener
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Fetch rooms once
  useEffect(() => {
    (async () => {
      try {
        const q = query(collection(db, 'rooms'), orderBy('name'));
        const snap = await getDocs(q);
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setRooms(data);
        console.log(data)
        setTypes(['All Rooms', ...Array.from(new Set(data.map(r => r.name)))]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Apply filters
  useEffect(() => {
    let tmp = [...rooms];
    if (selectedType !== 'All Rooms') {
      tmp = tmp.filter(r => r.name === selectedType);
    }
    if (priceRange !== 'all') {
      tmp = tmp.filter(r => {
        const p = Number(r.pricePerNight);
        if (priceRange === '0-2000')    return p <= 2000;
        if (priceRange === '2000-3000') return p >= 2000 && p <= 3000;
        if (priceRange === '3000+')     return p >= 3000;
        return true;
      });
    }
    setFiltered(tmp);
  }, [rooms, selectedType, priceRange]);

      const formatDateForOrder = (date) => {
    return date?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Render sidebar / offcanvas filters
  const renderFilters = () => (
    <>
      <div
        className="d-flex align-items-center gap-3 px-3 py-2 w-100 mb-2 search-box"
        style={{ maxWidth: '98%', cursor: 'pointer' }}
        onClick={() => { setShowSearchModal(true); setShowOffcanvas(false); }}
      >
        <Search size={17} className="text-secondary" />
        <div>
          <div className="fw-semibold" style={{ fontSize: 13 }}>
            {range.from && range.to
              ? `${range.from.toLocaleDateString()} → ${range.to.toLocaleDateString()}`
              : 'Select date range'}
          </div>
        <div className="text-secondary" style={{ fontSize: 13 }}>
              · {adults} Adult{adults > 1 ? "s" : ""}
              {children > 0 ? `, ${children} Child` : ""}
            </div>
        </div>
      </div>

      <h5 className="mb-3">Room Types</h5>
      <ListGroup variant="flush" className="mb-4">
        {types.map(type => (
          <ListGroup.Item
            key={type}
            action
            active={selectedType === type}
            variant={selectedType === type ? 'primary' : 'light'}
            onClick={() => { setSelectedType(type); setShowOffcanvas(false); }}
          >
            {type}
          </ListGroup.Item>
        ))}
      </ListGroup>

      <h5 className="mb-2">Filter by Price</h5>
      <Form.Select
        value={priceRange}
        onChange={e => { setPriceRange(e.target.value); setShowOffcanvas(false); }}
      >
        {PRICE_RANGES.map(r => (
          <option key={r.key} value={r.key}>{r.label}</option>
        ))}
      </Form.Select>
    </>
  );
  useEffect(() => {
      if (!range.from || !range.to) {
        setBookingCounts({});
        return;
      }
      (async () => {
           const checkinISO  = formatDateForOrder(range.from);
           const checkoutISO = formatDateForOrder(range.to);
           const bq = query(
             collection(db, 'room_bookings'),
             where('checkIn', '<', checkoutISO),
             where('checkOut','>', checkinISO),
            where('status', '==', 'booking-confirmed')
           );
        const snap = await getDocs(bq);
        const counts = {};
        snap.docs.forEach(doc => {
          const { roomId ,roomCount = 1 } = doc.data();
          counts[roomId] = (counts[roomId] || 0)+roomCount;
        });
        console.group("count",counts)
        setBookingCounts(counts);
      })();
    }, [range]);
  return (
    <Container fluid className="py-4" style={{ marginBottom: 60 }}>
      <Row>
        {/* Desktop Sidebar */}
        <Col md={3} className="d-none d-md-block" style={{ position: 'sticky', top: 20 }}>
          {renderFilters()}
        </Col>

        {/* Main */}
        <Col xs={12} md={9}>
          {loading ? (
            // Skeleton of 4 cards
            <Row className="g-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Col key={i} xs={12} md={6}>
                  <Card className="shadow-sm rounded overflow-hidden room-card">
                    <div
                      className="placeholder-glow"
                      style={{ height: 200, backgroundColor: '#e9ecef' }}
                    >
                      <Placeholder xs={12} animation="glow" />
                    </div>
                    <Card.Body>
                      <Placeholder animation="glow">
                        <Placeholder as={Card.Title} xs={8} />
                        <Placeholder xs={6} className="mt-2" />
                        <Placeholder xs={4} className="mt-2" />
                      </Placeholder>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : (
            <>
              {/* Mobile Filter Button */}
              {isMobile && (
                <Button
                  className="d-md-none btn-own"
                  onClick={() => setShowOffcanvas(true)}
                  style={{
                    position: 'fixed',
                    bottom: 80,
                    right: 20,
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    zIndex: 1000,
                  }}
                >
                  <Filter size={24} />
                </Button>
              )}

              {/* Rooms Grid */}
              <Row className="g-4">
                {filtered.map(room => {
                  const loaded = imgLoaded[room.id] === true;
                  return (
                    <Col key={room.id} xs={12} md={6}>
                      <Card className="shadow-sm rounded overflow-hidden room-card"
                      onClick={() => navigate(`/rooms/${room.id}`)} style={{ cursor: 'pointer' }}>
                      
                        <div className="position-relative">
                          {/* image skeleton */}
                          {!loaded && (
                            <div
                              className="placeholder-glow"
                              style={{ height: 200, backgroundColor: '#e9ecef' }}
                            >
                              {/* <Placeholder xs={12} animation="glow" /> */}
                              {/* <Skeleton  width={200} animation="glow"/> */}
                            </div>
                          )}
                          <Card.Img
                            variant="top"
                            src={room.roomImage}
                            onLoad={() =>
                              setImgLoaded(prev => ({ ...prev, [room.id]: true }))
                            }
                            style={{
                              height: 200,
                              objectFit: 'cover',
                              display: loaded ? 'block' : 'none',
                            }}
                          />
                          {loaded && (() => {
                            const booked     = bookingCounts[room.id] || 0;
                            const totalRooms = room.roomCount ;
                            const availableRooms =room.roomCount-booked;
                            const fullyBooked =booked >= totalRooms;
                            console.log(availableRooms)
                            if (fullyBooked) {
                              return (
                                <div className="fully-booked-label"><center>Fully booked</center></div>
                              );
                            } if (!fullyBooked && availableRooms){
                              return (
                                <div className="avilable-count-label"><center>Only {availableRooms} left</center></div>
                              );
                            }
                            return null;
                          })()}

                      

                        </div>

                        <Card.Body>
                          {!loaded ? (
                            <>
                              <Skeleton as={Card.Title}  width={200} animation="glow"/>
                              <Skeleton as={Card.Text}  width={100} animation="glow"/>
                             
                            </>
                          ) : (
                            <>
                              <div
                                className="d-flex justify-content-between align-items-center mb-1"
                                style={{ color: '#0f172a' }}
                              >
                                <b>{room.name}</b>
                              <div className="d-flex align-items-center ">
                                <div className="d-flex align-items-center me-3">
                                  <Users size={16} className="me-1" color='#54b435'/>
                                  <span>{room.occupancy} Adults</span>
                                </div>
                                <div className="d-flex align-items-center">
                                  <Baby size={16} className="me-1"color='#54b435'/>
                                  <span>{room.childOccupancy} Child</span>
                                </div>
                              </div>

                                
                              </div>
                              <div className="fw-bold mb-1" style={{ color: '#54b435' }}>
                                ₹{room.pricePerNight}{' '}
                                <small className="text-muted">
                                  / <CloudMoon /> night
                                </small>
                              </div>
                          
                                {/* ✅ New: Show how many rooms needed */}
                             {adults > 0 || children > 0 ? (() => {
  const requiredAdultsRooms = room.occupancy
    ? Math.ceil(adults / room.occupancy)
    : 0;
  const requiredChildRooms = room.childOccupancy
    ? Math.ceil(children / room.childOccupancy)
    : 0;

  const requiredRooms = Math.max(
    requiredAdultsRooms || 0,
    requiredChildRooms || 0
  );
console.log(requiredRooms)
  if (requiredRooms > 1) {
    return (
      <div
        className="text-mute mt-1"
        style={{ fontSize: 12, color: '#ffc107' }}
      >
        <CircleAlert size={13}/>You need {requiredRooms} {requiredRooms > 1 ? 'rooms' : 'room'} of this type
        to fit {adults} adult{adults > 1 ? 's' : ''}
        {children > 0 ? ` and ${children} child${children > 1 ? 'ren' : ''}` : ''}.
      </div>
    );
  }

  if (requiredRooms === 1) {
    return (
      <div
        className="mt-1"
        style={{ fontSize: 12, color: '#28a745', fontWeight: '500' }}
      >
        <CircleCheck size={13}/>Best choice for your group
      </div>
    );
  }

  return null;
})() : null}

        </>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}

                {filtered.length === 0 && (
                  <Col xs={12}>
                    <div className="text-center text-muted py-5">
                      No rooms match your filters.
                    </div>
                  </Col>
                )}
              </Row>
            </>
          )}
        </Col>
      </Row>

      {/* Mobile Offcanvas */}
      <Offcanvas show={showOffcanvas} onHide={() => setShowOffcanvas(false)} placement="end">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Filter Rooms</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>{renderFilters()}</Offcanvas.Body>
      </Offcanvas>

      {/* Search Modal / Offcanvas */}
      <SearchModal show={showSearchModal} onHide={() => setShowSearchModal(false)} />
    </Container>
  );
}
